import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { PaymentForm } from '@components/PaymentForm';
import { formatCurrency } from '@lib/utils';
import { ROUTES } from '@lib/routes';
import { sendBookingConfirmationNotifications } from '@lib/notifications';
import { useAuth } from '@hooks/useAuth';
import { useBooking, useConfirmBooking } from '@hooks/useBookings';
import { generateAndSaveQRCode } from '@lib/firebase/bookings';
import { useSquarePayment } from '@hooks/useSquare';

export function BookingConfirmPage(): JSX.Element {
  const location = useLocation();
  const { user } = useAuth();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const bookingData = location.state as {
    bookingId?: string;
    bikeId?: string;
    bikeName?: string;
    totalPrice?: number;
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    locationName?: string;
    insuranceIncluded?: boolean;
  } | null;

  const { data: booking } = useBooking(bookingData?.bookingId || null);
  const confirmBooking = useConfirmBooking();
  const processSquarePayment = useSquarePayment();
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Show payment form if booking is pending
  useEffect(() => {
    if (bookingData && user && booking?.status === 'pending' && !showPaymentForm) {
      setShowPaymentForm(true);
    }
  }, [bookingData, user, booking, showPaymentForm]);

  const handlePaymentSuccess = async (paymentId: string, sourceId: string) => {
    if (!bookingData?.bookingId || !user) {
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      // Process payment through Square
      const paymentResponse = await processSquarePayment.mutateAsync({
        sourceId,
        idempotencyKey: `booking_${bookingData.bookingId}_${Date.now()}`,
        amountMoney: {
          amount: bookingData.totalPrice || 0,
          currency: 'USD',
        },
        referenceId: bookingData.bookingId,
        note: `Booking payment for ${bookingData.bikeName}`,
      });

      const squarePaymentId = paymentResponse.payment?.id || paymentId;

      // Confirm booking with payment ID
      await confirmBooking.mutateAsync({
        bookingId: bookingData.bookingId,
        paymentId: squarePaymentId,
      });

      // Generate QR code
      if (user.id) {
        await generateAndSaveQRCode(bookingData.bookingId, user.id);
      }

      // Send confirmation notifications
      await sendBookingConfirmationNotifications({
        userId: user.id,
        userEmail: user.email || '',
        userPhone: user.phoneNumber || undefined,
        bookingId: bookingData.bookingId,
        bookingData: {
          bikeName: bookingData.bikeName || 'E-Bike',
          startDate: bookingData.startDate || '',
          endDate: bookingData.endDate || '',
          locationName: bookingData.locationName || '',
          totalPrice: bookingData.totalPrice || 0,
        },
      });

      setShowPaymentForm(false);
    } catch (error) {
      console.error('Failed to process booking:', error);
      setPaymentError(
        error instanceof Error ? error.message : 'Failed to process payment. Please contact support.'
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
  };

  if (!bookingData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Booking Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We couldn't find your booking information.
            </p>
            <Link to={ROUTES.CUSTOMER.SEARCH}>
              <Button>Search Hubs</Button>
            </Link>
          </Card>
        </div>
      </Layout>
    );
  }

  // Show payment form if booking is pending
  if (showPaymentForm && booking?.status === 'pending') {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <Card>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Complete Your Booking
              </h2>
              
              {paymentError && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-800 dark:text-red-200">{paymentError}</p>
                </div>
              )}

              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(bookingData.totalPrice || 0)}
                  </span>
                </div>
                {bookingData.bikeName && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Booking: {bookingData.bikeName}
                  </p>
                )}
              </div>

              <PaymentForm
                amount={bookingData.totalPrice || 0}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                isLoading={isProcessingPayment || processSquarePayment.isPending}
              />
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  if (isProcessingPayment || processSquarePayment.isPending) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Processing Payment...
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we confirm your booking.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  if (paymentError) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
              Payment Failed
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{paymentError}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTES.CUSTOMER.MY_BOOKINGS}>
                <Button>View My Bookings</Button>
              </Link>
              <Link to={ROUTES.CUSTOMER.SEARCH}>
                <Button variant="outline">Try Again</Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <Card className="text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900">
                <svg
                  className="h-8 w-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Booking Confirmed!
            </h1>

            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              Your booking has been confirmed. You'll receive a confirmation email shortly.
            </p>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-6 text-left">
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Booking ID:</span>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {bookingData.bookingId}
                  </p>
                </div>
                {bookingData.bikeName && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Bike:</span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {bookingData.bikeName}
                    </p>
                  </div>
                )}
                {bookingData.locationName && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Location:</span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {bookingData.locationName}
                    </p>
                  </div>
                )}
                {bookingData.startDate && bookingData.startTime && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Pickup:</span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {new Date(`${bookingData.startDate}T${bookingData.startTime}`).toLocaleString()}
                    </p>
                  </div>
                )}
                {bookingData.endDate && bookingData.endTime && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Return:</span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {new Date(`${bookingData.endDate}T${bookingData.endTime}`).toLocaleString()}
                    </p>
                  </div>
                )}
                {bookingData.totalPrice && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Paid:</span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {formatCurrency(bookingData.totalPrice)}
                    </p>
                  </div>
                )}
                {booking?.qrCode && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400 block mb-2">
                      Check-in QR Code:
                    </span>
                    <img
                      src={booking.qrCode}
                      alt="QR Code"
                      className="w-32 h-32 mx-auto border border-gray-300 dark:border-gray-600 rounded"
                    />
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
                      Show this QR code at check-in
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTES.CUSTOMER.MY_BOOKINGS}>
                <Button>View My Bookings</Button>
              </Link>
              <Link to={ROUTES.CUSTOMER.SEARCH}>
                <Button variant="outline">Browse More Bikes</Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

